import { DoorOpen, Laptop, Package, Users } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { cn, formatRelativeDate, initials } from '@/lib/utils';
import type { PersonaDentro } from '@/types/dominio';

interface Props {
  data: PersonaDentro[];
  loading?: boolean;
}

export function DentroAhoraPanel({ data, loading }: Props) {
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Dentro ahora</CardTitle>
            <CardDescription>Último movimiento sin salida registrada</CardDescription>
          </div>
          <Badge variant="outline" className="gap-1.5 font-semibold">
            <Users className="h-3.5 w-3.5" />
            {loading ? '—' : data.length}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {loading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
            <DoorOpen className="h-8 w-8 opacity-40" />
            <p className="text-sm">No hay personas dentro en este momento</p>
          </div>
        ) : (
          <ul className="divide-y divide-border max-h-[420px] overflow-y-auto -mx-2">
            {data.map((p) => (
              <li key={p.id_empleado} className="flex items-center gap-3 px-2 py-2.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold ring-1 ring-emerald-100">
                  {initials(p.nombres)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{p.nombres}</p>
                  <p className="text-xs text-muted-foreground tabular">CC {p.documento}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {p.con_equipo_corporativo && (
                    <span title="Con equipo corporativo" className="text-primary-600">
                      <Laptop className="h-4 w-4" />
                    </span>
                  )}
                  {p.con_equipo_foraneo && (
                    <span title="Con equipo foráneo" className="text-blue-600">
                      <Package className="h-4 w-4" />
                    </span>
                  )}
                  <span className={cn('text-xs text-muted-foreground whitespace-nowrap')}>
                    {formatRelativeDate(p.desde)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
